import { useEffect, useRef } from "react";
import { useSeo } from "../lib/useSeo";
import { Reveal } from "../components/Reveal";
import { Waitlist } from "../components/Waitlist";
import { Faq } from "../components/Faq";
import { GAME_LIBRARY, GAME_MARQUEE } from "../lib/games";
import { Bolt, Wallet, Scale, Shield, Globe, Power, Check, Arrow, Spark } from "../components/icons";

const HEADLINE = ["Any", "game.", "Any", "crew."];

export function Landing() {
  useSeo({
    title: "TheGameDude — Any game server, live in minutes",
    description:
      "Spin up a dedicated server for any game — Minecraft, Valheim, Rust, Palworld, ARK, CS2 and 20+ more. One click, zero ops, online in minutes. Cancel anytime.",
    path: "/",
  });

  const heroRef = useRef<HTMLElement | null>(null);
  const tiltRef = useRef<HTMLDivElement | null>(null);

  // Honor /#waitlist (and other in-page hashes) on initial load.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, []);

  // Cursor-follow spotlight + subtle terminal tilt (motion inspiration).
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        hero.style.setProperty("--mx", `${x * 100}%`);
        hero.style.setProperty("--my", `${y * 100}%`);
        if (tiltRef.current) {
          tiltRef.current.style.setProperty("--tx", String(x - 0.5));
          tiltRef.current.style.setProperty("--ty", String(y - 0.5));
        }
      });
    };
    hero.addEventListener("mousemove", onMove);
    return () => {
      hero.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div>
      {/* ---------------- Hero ---------------- */}
      <section className="hero" ref={heroRef as any}>
        <div className="hero-spotlight" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="hero-grid">
          <div>
            <span className="eyebrow"><span className="bar" /> 20+ games · one-click deploy</span>
            <h1>
              {HEADLINE.map((w, i) => (
                <span className="word" key={i} style={{ animationDelay: `${0.12 + i * 0.07}s` }}>
                  {w}{" "}
                </span>
              ))}
              <br />
              <span className="word" style={{ animationDelay: `${0.12 + HEADLINE.length * 0.07}s` }}>
                Server-up in <span className="sweep">minutes.</span>
              </span>
            </h1>
            <p className="lede">
              Pick a game, hit deploy, and we conjure a dedicated server out of thin air —
              installed, secured, and ready to share. No panels. No SSH. No "works on my
              machine." Just a link, and your crew's already loading in.
            </p>

            <div className="hero-cta">
              <a href="#waitlist" className="btn btn-primary btn-lg" data-testid="cta-get-started">
                Join the waitlist <Arrow size={16} />
              </a>
            </div>

            <div className="trustline">
              <span><Spark size={15} /> Launching soon</span>
              <span><Check size={15} /> Early-access perks</span>
              <span><Check size={15} /> No spam, ever</span>
            </div>
          </div>

          {/* Live provisioning terminal inside a glowing portal */}
          <Reveal delay={120} className="portal-wrap">
            <div className="portal" aria-hidden />
            <div ref={tiltRef} className="tilt">
              <ProvisionTerminal />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- Stats ---------------- */}
      <section className="section" style={{ paddingTop: 24 }}>
        <Reveal>
          <div className="stats">
            <Stat n="Minutes" l="From click to playable" />
            <Stat n="20+" l="Games and counting" />
            <Stat n="100s" l="Of players per world" />
            <Stat n="Zero" l="Ops knowledge required" />
          </div>
        </Reveal>
      </section>

      {/* ---------------- Dual marquee ---------------- */}
      <Reveal>
        <div className="marquee" aria-hidden>
          <div className="marquee-track">
            {[...GAME_MARQUEE, ...GAME_MARQUEE].map((g, i) => (
              <span className="marquee-item" key={i}><span className="pip" /> {g}</span>
            ))}
          </div>
        </div>
      </Reveal>
      <Reveal>
        <div className="marquee marquee-rev" aria-hidden style={{ borderTop: "none" }}>
          <div className="marquee-track">
            {[...[...GAME_MARQUEE].reverse(), ...[...GAME_MARQUEE].reverse()].map((g, i) => (
              <span className="marquee-item" key={i}><span className="pip" /> {g}</span>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ---------------- Games library ---------------- */}
      <section className="section" id="games">
        <Reveal>
          <div className="section-head">
            <span className="kicker">The library</span>
            <h2>If your crew plays it, we host it.</h2>
            <p className="sub">
              Survival, sandbox, shooters, co-op chaos — a deep, ever-growing roster. New
              titles drop constantly, and you can request the one you're itching for.
            </p>
          </div>
        </Reveal>
        <Reveal>
          <div className="games-grid" data-testid="games-grid">
            {GAME_LIBRARY.map((g) => (
              <div className="game-card" key={g.name}>
                <div className="cover">
                  {g.img ? (
                    <img src={g.img} alt={`${g.name} server hosting`} loading="lazy" width={460} height={215} />
                  ) : (
                    <div className="cover-mc">
                      <img src="/games/minecraft-creeper.svg" alt="Minecraft server hosting" loading="lazy" width={64} height={64} />
                    </div>
                  )}
                </div>
                <div className="meta">
                  <span className="gn">{g.name}</span>
                  <span className="gt">{g.tag}</span>
                </div>
              </div>
            ))}
            <a href="#waitlist" className="game-card more-card">Want your game?<br />Join the waitlist →</a>
          </div>
        </Reveal>
      </section>

      {/* ---------------- How it works ---------------- */}
      <section className="section" id="how">
        <Reveal>
          <div className="section-head">
            <span className="kicker">How it works</span>
            <h2>Three taps from "let's play" to playing.</h2>
            <p className="sub">We handle the servers, the installs, the networking — all of it.</p>
          </div>
        </Reveal>
        <div className="steps">
          {STEPS.map((s, i) => (
            <Reveal as="div" delay={i * 90} key={s.title}>
              <div className="step">
                <div className="num">{i + 1}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- Features ---------------- */}
      <section className="section">
        <Reveal>
          <div className="section-head">
            <span className="kicker">Why TheGameDude</span>
            <h2>The smoothest server you'll ever run.</h2>
            <p className="sub">Built so the only thing you think about is the game.</p>
          </div>
        </Reveal>
        <div className="grid grid-3">
          {FEATURES.map((f, i) => (
            <Reveal as="div" delay={i * 70} key={f.title}>
              <div className="card card-hover feature">
                <div className="ic">{f.icon}</div>
                <h3>{f.title}</h3>
                <p className="muted">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- Big hype band ---------------- */}
      <section className="band">
        <div className="container">
          <Reveal>
            <h2>Stop renting headaches. <span className="grad">Start playing.</span></h2>
            <p>
              No config files at 2am. No "why won't it connect." No surprise lag spikes mid-raid.
              Just a server that shows up, works, and gets out of your way.
            </p>
            <div className="hero-cta" style={{ justifyContent: "center" }}>
              <a href="#waitlist" className="btn btn-primary btn-lg">Join the waitlist <Arrow size={16} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="section" id="faq">
        <Reveal>
          <div className="section-head"><span className="kicker">FAQ</span><h2>Questions, answered.</h2></div>
        </Reveal>
        <Reveal><Faq /></Reveal>
      </section>

      {/* ---------------- Closing waitlist CTA ---------------- */}
      <section className="section" id="waitlist">
        <Reveal>
          <div className="waitlist">
            <span className="kicker">Early access</span>
            <h2>Be first in when your game drops.</h2>
            <p className="sub" style={{ maxWidth: 560 }}>
              We're onboarding crews region by region. Join the waitlist for early access, a
              founding-member perk, and a say in which games we add next.
            </p>
            <Waitlist />
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div className="stat">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
    </div>
  );
}

function ProvisionTerminal() {
  const lines: Array<{ t: string; cls?: string; pfx?: boolean }> = [
    { t: "gamedude deploy --game palworld --squad", pfx: true },
    { t: "✓ checkout confirmed", cls: "ok" },
    { t: "→ summoning a dedicated node …", cls: "dim" },
    { t: "→ installing game + opening ports", cls: "dim" },
    { t: "→ writing DNS · running healthcheck", cls: "dim" },
    { t: "✓ server live — ping your crew", cls: "ok" },
  ];
  return (
    <div className="terminal" data-testid="hero-terminal">
      <div className="terminal-bar">
        <i className="r" /><i className="y" /><i className="g" />
        <span className="ttl">play.thegamedude.org — live</span>
      </div>
      <div className="terminal-body">
        {lines.map((l, i) => (
          <div className="tline" key={i} style={{ animationDelay: `${0.5 + i * 0.55}s` }}>
            {l.pfx && <span className="pfx">$</span>}
            <span className={l.cls}>{l.t}</span>
          </div>
        ))}
        <div className="tline" style={{ animationDelay: `${0.5 + lines.length * 0.55}s` }}>
          <span className="pfx">›</span>
          <span className="addr">squad.play.thegamedude.org</span>
          <span className="caret" />
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  { title: "Pick your game", body: "Browse a huge library — survival, sandbox, shooters, co-op. Find your crew's obsession and hit deploy." },
  { title: "We do the heavy lifting", body: "A dedicated node spins up, the game installs, ports open, DNS writes. You watch it happen live." },
  { title: "Drop the link & play", body: "Grab your shareable address, paste it in the group chat, and you're in. Manage everything from one dashboard." },
];

const FEATURES = [
  { icon: <Bolt size={22} />, title: "Instant deploys", body: "No control panel maze, no SSH, no config files. Click a game and it's live before the snacks arrive." },
  { icon: <Scale size={22} />, title: "Scales with the squad", body: "From a chill duo world to a packed, fully-modded server — pick your vibe and grow whenever you want." },
  { icon: <Shield size={22} />, title: "Backups on autopilot", body: "Your worlds are safe. Snapshots run quietly in the background so a bad night never wipes the saga." },
  { icon: <Power size={22} />, title: "Total control", body: "Start, stop, restart, watch live console logs and roll back — all from a dashboard built for humans." },
  { icon: <Globe size={22} />, title: "Low-latency everywhere", body: "We put your server close to your crew, so it's smooth from the first spawn to the final boss." },
  { icon: <Wallet size={22} />, title: "Fair & flexible", body: "Simple monthly plans, no lock-in, cancel anytime. You only ever pay for a server that's actually yours." },
];
